/**
 * Authenticated fetch utilities using Shopify App Bridge
 * App Bridge automatically injects OIDC tokens for authentication
 */

/**
 * Performs an authenticated fetch request to the app's API
 * App Bridge automatically adds authentication headers
 *
 * @param url - API endpoint URL (relative or absolute)
 * @param options - Standard fetch options
 * @returns Promise with fetch response
 *
 * @example
 * ```ts
 * const response = await authenticatedFetch('/api/alerts');
 * const data = await response.json();
 * ```
 */
export async function authenticatedFetch(
  url: string,
  options?: RequestInit
): Promise<Response> {
  // Use shopify global fetch which automatically adds session token
  if (typeof window !== 'undefined' && window.shopify?.idToken) {
    const token = await window.shopify.idToken();
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options?.headers,
      },
    });
  }

  // Fallback for server-side or non-embedded contexts
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
}

/**
 * Shopify Admin API GraphQL query interface
 */
export interface GraphQLQuery {
  query: string;
  variables?: Record<string, any>;
}

/**
 * Shopify Admin API GraphQL response
 */
export interface GraphQLResponse<T = any> {
  data?: T;
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: string[];
  }>;
  extensions?: Record<string, any>;
}

/**
 * Performs a GraphQL query directly to Shopify Admin API
 * Uses App Bridge's special shopify:admin/api/... URL format
 *
 * @param query - GraphQL query string
 * @param variables - Query variables
 * @param apiVersion - Shopify API version (default: 2025-01)
 * @returns Promise with GraphQL response
 *
 * @example
 * ```ts
 * const result = await fetchAdminAPI<{ product: Product }>(`
 *   query GetProduct($id: ID!) {
 *     product(id: $id) {
 *       id
 *       title
 *       variants(first: 10) {
 *         edges {
 *           node {
 *             id
 *             inventoryQuantity
 *           }
 *         }
 *       }
 *     }
 *   }
 * `, {
 *   id: 'gid://shopify/Product/1234567890'
 * });
 * ```
 */
export async function fetchAdminAPI<T = any>(
  query: string,
  variables?: Record<string, any>,
  apiVersion: string = '2025-01'
): Promise<GraphQLResponse<T>> {
  const response = await fetch(`shopify:admin/api/${apiVersion}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (!response.ok) {
    throw new Error(`Admin API request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Performs a REST API call to Shopify Admin API
 * Uses App Bridge's special shopify:admin/api/... URL format
 *
 * @param endpoint - REST API endpoint (e.g., '/products.json')
 * @param options - Fetch options
 * @param apiVersion - Shopify API version (default: 2025-01)
 * @returns Promise with response
 *
 * @example
 * ```ts
 * const products = await fetchAdminREST('/products.json?limit=10');
 * ```
 */
export async function fetchAdminREST(
  endpoint: string,
  options?: RequestInit,
  apiVersion: string = '2025-01'
): Promise<Response> {
  // Ensure endpoint starts with /
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  return fetch(`shopify:admin/api/${apiVersion}${cleanEndpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
}

/**
 * Helper to check if fetch is authenticated via App Bridge
 * Returns true if running in Shopify embedded context with App Bridge
 */
export function isAuthenticatedFetchAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  return window.shopify?.config?.apiKey !== undefined;
}

/**
 * Error class for authenticated fetch failures
 */
export class AuthenticatedFetchError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: Response
  ) {
    super(message);
    this.name = 'AuthenticatedFetchError';
  }
}

/**
 * Helper to handle fetch errors consistently
 */
export async function handleFetchError(response: Response): Promise<never> {
  let errorMessage = `Request failed: ${response.status} ${response.statusText}`;

  try {
    const errorData: any = await response.json();
    if (errorData.error) {
      errorMessage = errorData.error;
    } else if (errorData.message) {
      errorMessage = errorData.message;
    }
  } catch {
    // Ignore JSON parse errors
  }

  throw new AuthenticatedFetchError(errorMessage, response.status, response);
}
